import { useState, useEffect } from 'react';
import { CalendarDay, CalendarPadding } from '../types/calendar.type';
import { formatDate } from '..//utils/calendar.utils';
export function useGetCalendarDays(
    currentDate: Date,
    selectedDate: string | null,
    isRange: boolean = false,
    startDate?: Date | null,
    endDate?: Date | null,
): CalendarDay[] {
    const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);

    useEffect(() => {
        const { leadingDays, trailingDays } = getCalendarPadding(currentDate);
        const generatedDays = generateCalendarDays(
            currentDate,
            selectedDate,
            leadingDays,
            trailingDays,
            isRange,
            startDate,
            endDate,
        );
        setCalendarDays(generatedDays);
    }, [currentDate, selectedDate, startDate, endDate]);
    return calendarDays;
}
export function generateCalendarDays(
    date: Date,
    selectedDate: string | null, // 'YYYY-MM-DD'
    leadingDays: number,
    trailingDays: number,
    isRange: boolean = false,
    startDate?: Date | null,
    endDate?: Date | null,
): CalendarDay[] {
    const days: CalendarDay[] = [];
    const year = date.getFullYear();
    const month = date.getMonth();

    // Add leading days from previous month
    const prevMonth = new Date(year, month, 0); // last day of previous month
    const prevMonthDays = prevMonth.getDate();
    for (let i = leadingDays - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevMonthDays - i);
        days.push(
            createDate(date, false, selectedDate, isRange, startDate, endDate),
        );
    }

    // Add current month days
    const currentMonthDays = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= currentMonthDays; i++) {
        const date = new Date(year, month, i);
        days.push(
            createDate(date, true, selectedDate, isRange, startDate, endDate),
        );
    }

    // Add trailing days from next month
    for (let i = 1; i <= trailingDays; i++) {
        const date = new Date(year, month + 1, i);
        days.push(
            createDate(date, false, selectedDate, isRange, startDate, endDate),
        );
    }

    return days;
}
export function createDate(
    date: Date,
    currentMonth: boolean,
    selectedDate: string | null,
    isRange: boolean = false,
    startDate?: Date | null,
    endDate?: Date | null,
): CalendarDay {
    startDate = startDate ? startDate : null;
    endDate = endDate ? endDate : null;

    return {
        date: formatDate(date),
        isCurrentMonth: currentMonth,
        isSelected: currentMonth
            ? isSelectedDate(
                  date,
                  startDate,
                  endDate,
                  selectedDate || '',
                  isRange,
              )
            : false,
        isRangeDate:
            !isRange || !currentMonth
                ? false
                : startDate && endDate
                  ? isDateBetween(date, startDate, endDate)
                  : false,
    };
}

export function getCalendarPadding(date: Date): CalendarPadding {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const firstDayOfWeek = firstOfMonth.getDay(); // Sunday = 0

    const lastOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastOfMonth.getDate();

    const leadingDays = firstDayOfWeek;
    const totalDaysDisplayed = leadingDays + daysInMonth;

    // Determine if 5 or 6 weeks are needed
    const weeks = Math.ceil(totalDaysDisplayed / 7);
    const totalCells = weeks * 7;
    const trailingDays = totalCells - totalDaysDisplayed;

    return { leadingDays, trailingDays, totalCells, weeks };
}
export function isSelectedDate(
    date: Date,
    startDate: Date | null,
    endDate: Date | null,
    selectedDate: string,
    isRange = false,
): boolean {
    const d = formatDate(date);
    const s = startDate ? formatDate(startDate) : null;
    const e = endDate ? formatDate(endDate) : null;

    if (d === selectedDate) return true;

    if (isRange && (d === s || d === e)) return true;

    return false;
}

export function isDateBetween(
    targetDate: Date | string,
    startDate: Date | string,
    endDate: Date | string,
): boolean {
    const target = new Date(targetDate).getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    return target >= start && target <= end;
}
